<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main id="main-content" class="flex-1 overflow-auto py-8">
      <div class="max-w-5xl mx-auto px-5">

        <!-- Breadcrumb -->
        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer">Dashboard</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">Submit PCN</span>
        </nav>

        <!-- Header -->
        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-start justify-between">
              <div>
                <h1 class="text-3xl font-bold text-accent mb-2">
                  Project Concept Note (PCN)
                </h1>
                <p class="text-gray-600 mb-4">
                  The PCN is the first formal step in the Article 6.4 process. Submit your project concept to the host country DNA and the Supervisory Body before developing a full Project Design Document.
                </p>
                <div class="flex items-center space-x-4 text-sm">
                  <span class="flex items-center text-primary">
                    <i class="fas fa-dollar-sign mr-1"></i>
                    Submission Fee: $200.00
                  </span>
                  <span class="flex items-center text-gray-600">
                    <i class="fas fa-clock mr-1"></i>
                    DNA Review: 30 business days
                  </span>
                  <span class="flex items-center text-gray-600">
                    <i class="fas fa-info-circle mr-1"></i>
                    Article 6.4 Mechanism
                  </span>
                </div>
              </div>
              <div class="text-right">
                <div class="bg-primary/10 px-3 py-1 rounded-full text-primary text-sm font-medium mb-2">
                  Developer ID: DIN-2024-001247
                </div>
                <button class="text-primary text-sm hover:underline">
                  <i class="fas fa-save mr-1"></i>Save Draft
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- Workflow progress info -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <p class="text-sm font-medium text-blue-800 mb-2">
            <i class="fas fa-route mr-2"></i>Article 6.4 Project Workflow
          </p>
          <div class="flex items-center space-x-2 text-xs">
            <span class="bg-primary text-white px-2 py-1 rounded-full font-medium">PCN</span>
            <i class="fas fa-arrow-right text-gray-400"></i>
            <span class="text-gray-500 px-2 py-1">Host Country LoA</span>
            <i class="fas fa-arrow-right text-gray-400"></i>
            <span class="text-gray-500 px-2 py-1">PIN</span>
            <i class="fas fa-arrow-right text-gray-400"></i>
            <span class="text-gray-500 px-2 py-1">PDD</span>
            <i class="fas fa-arrow-right text-gray-400"></i>
            <span class="text-gray-500 px-2 py-1">DOE Validation</span>
            <i class="fas fa-arrow-right text-gray-400"></i>
            <span class="text-gray-500 px-2 py-1">Registration</span>
          </div>
        </div>

        <form class="space-y-8">

          <!-- Section 1: Project Concept -->
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-6 flex items-center">
              <i class="fas fa-lightbulb text-primary mr-3"></i>
              Project Concept
            </h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Project Name *</label>
                <input
                  type="text"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g. Mangrove Restoration Initiative – Borneo"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Project Sector / Type *</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Select sector</option>
                  <option>Afforestation / Reforestation</option>
                  <option>Improved Forest Management</option>
                  <option>Avoided Deforestation (REDD+)</option>
                  <option>Renewable Energy</option>
                  <option>Energy Efficiency</option>
                  <option>Methane Capture</option>
                  <option>Waste Management</option>
                  <option>Sustainable Agriculture</option>
                  <option>Blue Carbon (Coastal / Marine)</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Host Country *</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Select host country</option>
                  <option>Brazil</option>
                  <option>Indonesia</option>
                  <option>Kenya</option>
                  <option>Colombia</option>
                  <option>India</option>
                  <option>Ghana</option>
                  <option>Peru</option>
                  <option>Vietnam</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Project Region / Location *</label>
                <input
                  type="text"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g. Kalimantan, East Borneo"
                />
              </div>
              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">Project Description *</label>
                <textarea
                  rows="4"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Provide a brief description of the project concept, its objectives, and how it will reduce or remove greenhouse gas emissions."
                ></textarea>
              </div>
            </div>
          </section>

          <!-- Section 2: Preliminary Estimates -->
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-6 flex items-center">
              <i class="fas fa-chart-bar text-primary mr-3"></i>
              Preliminary Estimates
            </h2>
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Estimated Annual Reductions (tCO2e) *</label>
                <input
                  type="number"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="0"
                />
                <p class="text-xs text-gray-500 mt-1">Indicative estimate only</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Indicative Project Duration (years) *</label>
                <input
                  type="number"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="0"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Total Estimated Reductions (tCO2e)</label>
                <input
                  type="number"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                  placeholder="Auto-calculated"
                  readonly
                />
              </div>
              <div class="lg:col-span-3">
                <label class="block text-sm font-medium text-gray-700 mb-2">Indicative Methodology *</label>
                <input
                  type="text"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g. AR-ACM0003, VM0007, or describe the approach if not yet confirmed"
                />
              </div>
            </div>
          </section>

          <!-- Section 3: Host Country / DNA -->
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-6 flex items-center">
              <i class="fas fa-landmark text-primary mr-3"></i>
              Host Country / DNA Information
            </h2>
            <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p class="text-sm text-yellow-800">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                <strong>Required for Article 6.4:</strong> Your PCN must be submitted to your host country's Designated National Authority (DNA) for authorization. Provide the DNA contact details below.
              </p>
            </div>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">DNA Name / Authority *</label>
                <input
                  type="text"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g. Ministry of Environment and Forestry"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">DNA Contact Email *</label>
                <input
                  type="email"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="dna@ministry.gov"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Intended Submission Date to DNA *</label>
                <input
                  type="date"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">NDC Alignment</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Select NDC alignment</option>
                  <option>Directly contributes to host country NDC target</option>
                  <option>Partially aligned with NDC</option>
                  <option>Not directly linked to NDC target</option>
                </select>
              </div>
            </div>
          </section>

          <!-- Section 4: Stakeholder Overview -->
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-6 flex items-center">
              <i class="fas fa-users text-primary mr-3"></i>
              Stakeholder Overview
            </h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">Affected Communities / Stakeholders *</label>
                <textarea
                  rows="3"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Briefly identify the communities and stakeholders affected by or involved in this project."
                ></textarea>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Initial Stakeholder Engagement Status *</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Select status</option>
                  <option>Not yet commenced</option>
                  <option>Initial contact made</option>
                  <option>Consultations in progress</option>
                  <option>FPIC obtained</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Indigenous Peoples Involved?</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Select</option>
                  <option>Yes – FPIC process required</option>
                  <option>No</option>
                  <option>Under assessment</option>
                </select>
              </div>
            </div>
          </section>

          <!-- Section 5: Document Upload -->
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-6 flex items-center">
              <i class="fas fa-upload text-primary mr-3"></i>
              Document Upload
            </h2>
            <div class="space-y-6">
              <div class="border border-gray-200 rounded-lg p-4">
                <div class="flex items-center justify-between mb-3">
                  <h3 class="font-medium text-accent">PCN Form Document *</h3>
                  <span class="text-xs text-error">Required</span>
                </div>
                <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer">
                  <i class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
                  <p class="text-gray-600 mb-2">Drag and drop or click to upload</p>
                  <p class="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
                </div>
              </div>
              <div class="border border-gray-200 rounded-lg p-4">
                <div class="flex items-center justify-between mb-3">
                  <h3 class="font-medium text-accent">Proof of DNA Pre-notification (if available)</h3>
                  <span class="text-xs text-gray-400">Optional</span>
                </div>
                <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer">
                  <i class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
                  <p class="text-gray-600 mb-2">Drag and drop or click to upload</p>
                  <p class="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
                </div>
              </div>
            </div>
          </section>

          <!-- Section 6: Declarations -->
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-6 flex items-center">
              <i class="fas fa-check-circle text-primary mr-3"></i>
              Declarations
            </h2>
            <div class="space-y-4">
              <label class="flex items-start space-x-3 cursor-pointer">
                <input type="checkbox" class="mt-1 rounded border-gray-300 text-primary focus:ring-primary" />
                <span class="text-sm text-gray-700">I declare that the information provided in this PCN is accurate and complete to the best of my knowledge.</span>
              </label>
              <label class="flex items-start space-x-3 cursor-pointer">
                <input type="checkbox" class="mt-1 rounded border-gray-300 text-primary focus:ring-primary" />
                <span class="text-sm text-gray-700">I confirm that this project concept will be submitted to the host country DNA for authorization and that corresponding adjustments will be requested.</span>
              </label>
              <label class="flex items-start space-x-3 cursor-pointer">
                <input type="checkbox" class="mt-1 rounded border-gray-300 text-primary focus:ring-primary" />
                <span class="text-sm text-gray-700">I acknowledge that submission of this PCN does not constitute project registration and that full Article 6.4 Supervisory Body rules apply throughout the project lifecycle.</span>
              </label>
            </div>
          </section>

          <!-- Section 7: Actions -->
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div class="flex items-center space-x-4">
                <button type="button" class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                  <i class="fas fa-save mr-2"></i>Save Draft
                </button>
                <button type="button" class="px-6 py-2 border border-primary text-primary rounded-md hover:bg-primary/5">
                  <i class="fas fa-eye mr-2"></i>Preview
                </button>
              </div>
              <div class="flex items-center space-x-4">
                <div class="text-right text-sm text-gray-600">
                  <p>Submission Fee <span class="font-semibold text-primary">$200.00</span></p>
                  <p class="text-xs">Payment processed on submission</p>
                </div>
                <button type="submit" class="px-8 py-3 bg-primary text-white rounded-md font-medium hover:bg-primary/90 flex items-center">
                  <i class="fas fa-paper-plane mr-2"></i>Submit PCN
                </button>
              </div>
            </div>
          </section>

        </form>
      </div>
    </main>
  </div>
</template>
